/**
 * useQueryLogs Hook
 * React hook for managing database query performance log streaming
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useCCMS } from '../../../react/hooks/useCCMS';
import { QueryLogsClient } from '../QueryLogsClient';
import { ConnectionState } from '../../shared/types';
/**
 * Hook for query logs streaming
 */
export function useQueryLogs(options = {}) {
    const { config } = useCCMS();
    const [logs, setLogs] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [error, setError] = useState(null);
    const clientRef = useRef(null);
    const { autoConnect = false, filter = {}, maxBufferSize = 1000, onLog, autoLoadRecent = true, recentLogsLimit = 100, } = options;
    const connect = useCallback(async () => {
        if (clientRef.current?.isConnected()) {
            return;
        }
        try {
            if (!clientRef.current) {
                clientRef.current = new QueryLogsClient(config.logsUrl || config.baseUrl, async () => {
                    if (config.getToken) {
                        return await config.getToken();
                    }
                    return null;
                }, { maxBufferSize });
                clientRef.current.onStateChange((state, err) => {
                    setIsConnected(state === ConnectionState.Connected);
                    if (err) {
                        setError(err);
                    }
                });
                clientRef.current.on('log', (log) => {
                    setLogs((prev) => {
                        const updated = [...prev, log];
                        return updated.slice(-maxBufferSize);
                    });
                    onLog?.(log);
                });
            }
            await clientRef.current.connect();
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, [config, maxBufferSize, onLog]);
    const disconnect = useCallback(async () => {
        if (clientRef.current) {
            await clientRef.current.disconnect();
            setIsConnected(false);
            setIsSubscribed(false);
            setLogs([]);
            setError(null);
        }
    }, []);
    const subscribe = useCallback(async (customFilter) => {
        if (!clientRef.current?.isConnected()) {
            await connect();
        }
        try {
            await clientRef.current.subscribe(customFilter || filter);
            setIsSubscribed(true);
            if (autoLoadRecent) {
                const recentLogs = await clientRef.current.getRecent(recentLogsLimit);
                setLogs(recentLogs);
            }
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, [connect, filter, autoLoadRecent, recentLogsLimit]);
    const unsubscribe = useCallback(async () => {
        if (clientRef.current && isSubscribed) {
            try {
                await clientRef.current.unsubscribe();
                setIsSubscribed(false);
            }
            catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                setError(error);
                throw error;
            }
        }
    }, [isSubscribed]);
    const clearLogs = useCallback(() => {
        setLogs([]);
        clientRef.current?.clearLogs();
    }, []);
    const loadRecent = useCallback(async (limit) => {
        if (!clientRef.current?.isConnected()) {
            throw new Error('Not connected to logs hub');
        }
        try {
            const recentLogs = await clientRef.current.getRecent(limit || recentLogsLimit);
            setLogs(recentLogs);
            return recentLogs;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, [recentLogsLimit]);
    useEffect(() => {
        if (autoConnect) {
            connect().catch((err) => {
                console.error('[useQueryLogs] Auto-connect failed:', err);
            });
        }
        return () => {
            disconnect();
        };
    }, [autoConnect]);
    return {
        logs,
        isConnected,
        isSubscribed,
        error,
        connect,
        disconnect,
        subscribe,
        unsubscribe,
        clearLogs,
        loadRecent,
    };
}
//# sourceMappingURL=useQueryLogs.js.map